import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";

import { useTrialStatus } from "../contexts/BalanceContext";
import { TrialBanner } from "./TrailBanner";
import { User } from "../types";
import Navigation from "./Navigation";

interface LayoutProps {
  children: React.ReactNode;
}

const getDefaultRouteForUser = (user: User | null) => {
  if (!user) {
    return "/auth/login";
  }

  if (user.is_super_admin) {
    return "/super-admin/dashboard";
  }

  return user.company_id ? "/company/dashboard" : "/candidate/dashboard";
};

const isRouteAllowedForUser = (pathname: string, user: User | null) => {
  if (!user) {
    return false;
  }

  if (pathname === "/dashboard") {
    return false;
  }

  if (pathname.startsWith("/super-admin")) {
    return user.is_super_admin;
  }

  if (pathname.startsWith("/company")) {
    return !!user.company_id && !user.is_super_admin;
  }

  if (pathname.startsWith("/candidate")) {
    return !user.company_id && !user.is_super_admin;
  }

  return true;
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleTrialStatus, trailStatus } = useTrialStatus();
  const [profileCheckComplete, setProfileCheckComplete] = useState(false);

  useEffect(() => {
    console.log("🔍 Layout: useEffect triggered", {
      loading,
      user: user?.email,
      isAuthenticated,
      pathname: location.pathname,
      userCompanyId: user?.company_id,
      userIsSuperAdmin: user?.is_super_admin,
      userRole: user?.role,
    });

    // Don't redirect if still loading authentication
    if (loading) {
      console.log("⏳ Layout: Still loading auth, waiting...");
      return;
    }

    // Check authentication first - if not authenticated, redirect to login
    if (!isAuthenticated || !user) {
      console.log("🚫 Layout: User not authenticated, redirecting to login");
      navigate("/auth/login", { replace: true });
      return;
    }

    const defaultRoute = getDefaultRouteForUser(user);
    const isAllowedRoute = isRouteAllowedForUser(location.pathname, user);

    if (!isAllowedRoute) {
      console.log("ðŸš« Layout: User not authorized for current route", {
        pathname: location.pathname,
        redirectingTo: defaultRoute,
        persona: user.persona,
      });
      navigate(defaultRoute, { replace: true });
      return;
    }

    // Debug the condition check
    const isJobSeeker = user && !user.company_id && !user.is_super_admin;
    console.log("🔍 Layout: Condition check:", {
      hasUser: !!user,
      hasCompanyId: !!user?.company_id,
      isSuperAdmin: !!user?.is_super_admin,
      isJobSeeker: isJobSeeker,
    });

    // Check if user is a job seeker who needs onboarding
    if (isJobSeeker) {
      console.log(
        "👤 Layout: User is a job seeker, checking profile completion...",
      );
      checkProfileCompletionStatus();
    } else {
      console.log(
        "🏢 Layout: User is company user or super admin, no profile check needed",
      );
      setProfileCheckComplete(true);
    }
    handleTrialStatus();
  }, [user, loading, isAuthenticated, navigate, location]);

  const checkProfileCompletionStatus = async () => {
    try {
      console.log("🔍 Layout: Starting profile completion check...");

      // Check sessionStorage first for quick response
      const hasCompletedOnboarding = sessionStorage.getItem(
        "profile_creation_completed",
      );
      const hasSkippedOnboarding = sessionStorage.getItem("skip_onboarding");

      console.log("📦 Layout: SessionStorage flags:", {
        hasCompletedOnboarding,
        hasSkippedOnboarding,
      });

      // Don't redirect if they're already on onboarding pages or auth pages
      const isOnOnboardingPage = location.pathname.includes(
        "/candidate/onboarding",
      );
      const isOnAuthPage = location.pathname.includes("/auth");
      const isOnProfilePage = location.pathname.includes("/candidate/profile");
      const isOnAccountPage =
        location.pathname.includes("/candidate/account-settings") ||
        location.pathname.includes("/candidate/change-password");
      const isOnDashboard = location.pathname === "/candidate/dashboard";

      console.log("🛣️ Layout: Current page analysis:", {
        pathname: location.pathname,
        isOnOnboardingPage,
        isOnAuthPage,
        isOnProfilePage,
        isOnAccountPage,
        isOnDashboard,
      });

      if (hasCompletedOnboarding || hasSkippedOnboarding) {
        console.log(
          "✅ Layout: User has completed onboarding or skipped (from sessionStorage)",
        );
        setProfileCheckComplete(true);
        return;
      }

      // If sessionStorage doesn't have the flag, check the backend
      console.log(
        "🔍 Layout: SessionStorage empty, checking backend for profile...",
      );
      const profileCheck = await apiService.checkCandidateProfileExists();
      console.log("📊 Layout: Backend profile check result:", profileCheck);

      if (
        profileCheck.has_profile &&
        (profileCheck.profile_type === "self_owned" ||
          profileCheck.profile_type === "job_seeker")
      ) {
        console.log(
          "✅ Layout: User has complete profile in backend, setting completion flag",
        );
        sessionStorage.setItem("profile_creation_completed", "true");
        setProfileCheckComplete(true);
        return;
      }

      if (profileCheck.has_profile && profileCheck.can_claim) {
        console.log(
          "🎯 Layout: User has company-owned profile that can be claimed, not setting completion flag",
        );
        setProfileCheckComplete(true);
        return;
      }

      // User doesn't have a profile and hasn't skipped onboarding
      if (
        !isOnOnboardingPage &&
        !isOnAuthPage &&
        !isOnProfilePage &&
        !isOnAccountPage &&
        !isOnDashboard
      ) {
        console.log("🔄 Layout: Redirecting job seeker to onboarding flow");
        navigate("/candidate/onboarding", { replace: true });
      } else {
        console.log(
          "🚫 Layout: Not redirecting - user is already on appropriate page",
        );
      }

      setProfileCheckComplete(true);
    } catch (error) {
      console.error(
        "❌ Layout: Error checking profile completion status:",
        error,
      );
      // On error, don't redirect to avoid blocking the user
      setProfileCheckComplete(true);
    }
  };

  // Show loading while authentication is being checked
  if (loading) {
    console.log("⏳ Layout: Showing auth loading spinner...");
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const isAuthorizedForCurrentRoute = isRouteAllowedForUser(
    location.pathname,
    user,
  );

  if (!isAuthenticated || !user || !isAuthorizedForCurrentRoute) {
    return null;
  }

  // Show loading while checking profile status for job seekers
  if (
    !profileCheckComplete &&
    user &&
    !user.company_id &&
    !user.is_super_admin
  ) {
    console.log("⏳ Layout: Showing profile check loading spinner...");
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Checking profile...</p>
        </div>
      </div>
    );
  }

  console.log("✅ Layout: Rendering layout with children");
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      {/* is_trail true  show msg  and  add on subscribtion button */}
      <TrialBanner></TrialBanner>

      <main>{children}</main>
    </div>
  );
};

export default Layout;
